import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, tokens, type CurrentUser } from './api';

/**
 * The dashboard's session. Signing in names the Executive Dashboard, with the same email and password as
 * the admin; the API lets in only an active Administrator who was given Dashboard Access in the admin, and
 * checks both again on every request — so a revoked grant signs this browser out on its next request.
 */

interface AuthContextValue {
  user: CurrentUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  // Nothing one session fetched may show in the next one on the same browser.
  const forget = useCallback(() => queryClient.clear(), [queryClient]);

  const load = useCallback(async () => {
    if (!tokens.access) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      setUser(await api.get<CurrentUser>('/auth/me'));
    } catch {
      tokens.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const onSignedOut = () => {
      forget();
      setUser(null);
    };
    window.addEventListener('viganium:signed-out', onSignedOut);
    return () => window.removeEventListener('viganium:signed-out', onSignedOut);
  }, [load, forget]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await api.post<{ accessToken: string; refreshToken: string; user: CurrentUser }>('/auth/login', {
        email,
        password,
        app: 'DASHBOARD',
      });
      tokens.set(result.accessToken, result.refreshToken);
      forget();
      setUser(result.user);
    },
    [forget],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post('/auth/logout', { refreshToken: tokens.refresh ?? undefined });
    } catch {
      // Signing out here matters more than the server round-trip succeeding.
    }
    tokens.clear();
    forget();
    setUser(null);
  }, [forget]);

  const value = useMemo(() => ({ user, loading, signIn, signOut }), [user, loading, signIn, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}
